/**
 * WingsSystem.js - Premium convenience currency
 *
 * Wings are earned through milestones and spent on QoL features:
 * - Auto-generate 5 optimized routes (closest airports you don't have)
 * - Replace stale routes with fresh ones
 */

import { getRouteKey, getRouteDemand } from "./RouteDemandSystem.js";
import { AIRCRAFT_DATABASE } from "../data/aircraft.js";
import { debug } from '../lib/debug.js';

// ============================================================================
// CONFIGURATION
// ============================================================================

export const WINGS_CONFIG = {
  startingBalance: 20,           // Starting Wings for new players
  routeGenerationCost: 1,        // 1 Wing = 5 new routes
  flightsPerWing: 50,            // Earn 1 Wing per 50 completed flights (passive)
  staleThreshold: 50,            // Routes with demand < 50% are "stale"
  routesPerWing: 5,              // Each Wing creates 5 routes
  abandonedRouteCooldownDays: 4, // Days before abandoned route demand recovers
  autoDepartCost: 5,             // 5 Wings to enable auto-depart
  autoDepartDurationMs: 3 * 60 * 1000, // 3 real minutes
};

// ============================================================================
// MILESTONES - One-time Wing rewards
// ============================================================================

export const MILESTONES = {
  // Flight milestones
  flights_100: { type: 'flights', threshold: 100, wings: 8, label: '100 Flights' },
  flights_500: { type: 'flights', threshold: 500, wings: 15, label: '500 Flights' },
  flights_1000: { type: 'flights', threshold: 1000, wings: 25, label: '1,000 Flights' },
  flights_5000: { type: 'flights', threshold: 5000, wings: 40, label: '5,000 Flights' },
  flights_10000: { type: 'flights', threshold: 10000, wings: 60, label: '10,000 Flights' },

  // Hub milestones
  hub_first: { type: 'hubs', threshold: 1, wings: 15, label: 'First Hub' },
  hub_5: { type: 'hubs', threshold: 5, wings: 30, label: '5 Hubs' },
  hub_10: { type: 'hubs', threshold: 10, wings: 50, label: '10 Hubs' },

  // Revenue milestones
  revenue_100m: { type: 'revenue', threshold: 100000000, wings: 15, label: '$100M Revenue' },
  revenue_1b: { type: 'revenue', threshold: 1000000000, wings: 30, label: '$1B Revenue' },
  revenue_10b: { type: 'revenue', threshold: 10000000000, wings: 50, label: '$10B Revenue' },

  // EOS milestone
  eos_achieved: { type: 'eos', threshold: 1, wings: 75, label: 'EOS Achieved' },

  // Fleet milestones
  fleet_10: { type: 'fleet', threshold: 10, wings: 10, label: '10 Aircraft' },
  fleet_50: { type: 'fleet', threshold: 50, wings: 25, label: '50 Aircraft' },
  fleet_100: { type: 'fleet', threshold: 100, wings: 40, label: '100 Aircraft' },
};

// ============================================================================
// STATE INITIALIZATION
// ============================================================================

export function createWingsState() {
  return {
    balance: WINGS_CONFIG.startingBalance,
    lifetimeEarned: WINGS_CONFIG.startingBalance,
    lifetimeSpent: 0,
    flightsSinceLastWing: 0,
    claimedMilestones: [], // Array of milestone IDs already claimed
  };
}

export function getWingsState(worldState) {
  return worldState?.wings || createWingsState();
}

// ============================================================================
// MILESTONE CHECKING
// ============================================================================

/**
 * Check and award any unclaimed milestones
 * @returns {Array} Array of newly claimed milestone objects
 */
export function checkMilestones(worldState) {
  const wings = getWingsState(worldState);
  const claimed = wings.claimedMilestones || [];
  const newClaims = [];

  for (const [id, milestone] of Object.entries(MILESTONES)) {
    // Skip already claimed
    if (claimed.includes(id)) continue;

    // Check if threshold met
    let currentValue = 0;
    switch (milestone.type) {
      case 'flights':
        currentValue = worldState?.stats?.completedFlights || 0;
        break;
      case 'hubs':
        currentValue = (worldState?.ownedHubs || []).length;
        break;
      case 'revenue':
        currentValue = worldState?.stats?.totalRevenue || 0;
        break;
      case 'eos':
        currentValue = worldState?.eos?.hasEOS ? 1 : 0;
        break;
      case 'fleet':
        currentValue = (worldState?.aircraft || []).length;
        break;
      default:
        continue;
    }

    if (currentValue >= milestone.threshold) {
      newClaims.push({ id, ...milestone });
    }
  }

  return newClaims;
}

/**
 * Process and award pending milestones
 * @param {Function} setWorldState - State setter
 * @returns {Array} Newly awarded milestones
 */
export function processMilestones(worldState, setWorldState) {
  const newClaims = checkMilestones(worldState);

  if (newClaims.length === 0) return [];

  const totalWings = newClaims.reduce((sum, m) => sum + m.wings, 0);
  const claimIds = newClaims.map(m => m.id);

  setWorldState(prev => {
    const wings = getWingsState(prev);
    return {
      ...prev,
      wings: {
        ...wings,
        balance: (wings.balance || 0) + totalWings,
        lifetimeEarned: (wings.lifetimeEarned || 0) + totalWings,
        claimedMilestones: [...(wings.claimedMilestones || []), ...claimIds],
      },
    };
  });

  return newClaims;
}

// ============================================================================
// PASSIVE FLIGHT-BASED EARNINGS
// ============================================================================

export function processFlightComplete(worldState, setWorldState) {
  const wings = getWingsState(worldState);
  const newFlightCount = (wings.flightsSinceLastWing || 0) + 1;

  if (newFlightCount >= WINGS_CONFIG.flightsPerWing) {
    setWorldState(prev => ({
      ...prev,
      wings: {
        ...getWingsState(prev),
        balance: (getWingsState(prev).balance || 0) + 1,
        lifetimeEarned: (getWingsState(prev).lifetimeEarned || 0) + 1,
        flightsSinceLastWing: 0,
      },
    }));
    return true;
  } else {
    setWorldState(prev => ({
      ...prev,
      wings: {
        ...getWingsState(prev),
        flightsSinceLastWing: newFlightCount,
      },
    }));
    return false;
  }
}

// ============================================================================
// ROUTE GENERATION
// ============================================================================

/**
 * Calculate distance between two airports using Haversine
 */
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 3440.065; // Earth radius in nautical miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

/**
 * Get the actual range for an aircraft, checking AIRCRAFT_DATABASE
 */
function getAircraftRange(aircraft) {
  // First check if range is set on the aircraft itself
  if (aircraft.range && aircraft.range > 0) {
    return aircraft.range;
  }
  // Otherwise look up in AIRCRAFT_DATABASE
  const type = aircraft.type || aircraft.model;
  const dbEntry = AIRCRAFT_DATABASE[type];
  if (dbEntry?.range) {
    return dbEntry.range;
  }
  // Default fallback
  return 1500;
}

/**
 * Find airports the player doesn't have routes to
 * Picks RANDOM airports within the range of the player's shortest-ranged aircraft
 * Excludes airports too close to the hub (< 300nm) for variety
 * @param {string} selectedHub - Optional hub to generate routes from (defaults to primary hub)
 */
export function findNewRouteDestinations(worldState, airports, count = 5, selectedHub = null) {
  // Use selected hub, or fall back to primary hub
  const hub = selectedHub || worldState?.airline?.hub || 'KPDX';
  const existingRoutes = worldState?.routes || [];
  const playerAircraft = worldState?.aircraft || [];

  // Get hub coordinates
  const hubAirport = airports[hub];
  if (!hubAirport) {
    console.warn('[Wings] Hub airport not found:', hub);
    return [];
  }

  // Find the shortest range among player's aircraft at this hub (or any hub)
  // Uses AIRCRAFT_DATABASE for accurate range lookup
  const playerHubs = worldState?.ownedHubs || [worldState?.airline?.hub || 'KPDX'];
  const playerHubSet = new Set(playerHubs);

  // Only consider aircraft at a hub (not stranded elsewhere)
  const hubAircraft = playerAircraft.filter(a => {
    const location = a.location || 'KPDX';
    return playerHubSet.has(location);
  });

  let shortestRange = 1500;
  if (hubAircraft.length > 0) {
    shortestRange = Math.min(...hubAircraft.map(a => getAircraftRange(a)));
  }

  // Get all destinations player already has routes to (from this hub)
  const existingDestinations = new Set();
  for (const route of existingRoutes) {
    if (route.origin === hub) existingDestinations.add(route.destination);
    if (route.destination === hub) existingDestinations.add(route.origin);
  }

  // Find airports we DON'T have routes to
  const candidates = [];
  for (const [code, airport] of Object.entries(airports)) {
    // Skip the hub itself
    if (code === hub) continue;

    // Skip if we already have a route there
    if (existingDestinations.has(code)) continue;

    // Skip airports without coordinates
    if (!airport.lat || !airport.lon) continue;

    const distance = calculateDistance(
      hubAirport.lat, hubAirport.lon,
      airport.lat, airport.lon
    );

    // Skip routes too close to hub (< 300nm) - we want variety, not nearby airports
    if (distance < 300) continue;

    // Skip routes beyond the shortest plane's range
    if (distance > shortestRange) continue;

    candidates.push({ code, airport, distance });
  }

  // Shuffle candidates randomly instead of sorting by distance
  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
  }

  // Return random N
  return candidates.slice(0, count);
}

/**
 * Generate new routes from Wings
 * @param {string} selectedHub - Optional hub to generate routes from
 * @returns {Array} Created route objects
 */
export function generateRoutes(worldState, airports, count = 5, selectedHub = null) {
  const hub = selectedHub || worldState?.airline?.hub || 'KPDX';
  const destinations = findNewRouteDestinations(worldState, airports, count, hub);

  const newRoutes = destinations.map(dest => ({
    id: `route-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    origin: hub,
    destination: dest.code,
    name: `${airports[hub]?.city || hub} ↔ ${dest.airport.city || dest.code}`,
    demand: 100,
    active: true,
    aircraftId: null,
    autoDispatch: false,
    status: 'unassigned',
    lastFlightId: null,
    lastCompletedAt: null,
    totalFlights: 0,
    totalRevenue: 0,
    isBidirectional: true,
    currentLegOrigin: hub,
    currentLegDestination: dest.code,
    generatedByWings: true, // Mark as Wing-generated
  }));

  return newRoutes;
}

/**
 * Find stale routes (demand below threshold)
 */
export function findStaleRoutes(worldState) {
  const routes = worldState?.routes || [];
  const routeDemand = worldState?.routeDemand || {};

  return routes.filter(route => {
    const demand = getRouteDemand(route.origin, route.destination, routeDemand);
    return demand < WINGS_CONFIG.staleThreshold;
  });
}

/**
 * Spend a Wing to generate routes
 * @param {string} mode - 'new' for just new routes, 'replace' to also remove stale
 * @param {string} selectedHub - Optional hub to generate routes from
 * @returns {Object} { success, newRoutes, removedRoutes, error }
 */
export function spendWingOnRoutes(worldState, setWorldState, airports, mode = 'new', selectedHub = null) {
  const wings = getWingsState(worldState);

  if ((wings.balance || 0) < WINGS_CONFIG.routeGenerationCost) {
    return { success: false, error: 'Not enough Wings' };
  }

  // Find stale routes if replacing
  const staleRoutes = mode === 'replace' ? findStaleRoutes(worldState) : [];

  // Generate new routes from selected hub
  const newRoutes = generateRoutes(worldState, airports, WINGS_CONFIG.routesPerWing, selectedHub);

  if (newRoutes.length === 0) {
    return { success: false, error: 'No new routes available (you may have routes to all nearby airports)' };
  }

  const currentDay = worldState?.dayNumber || 1;
  const routeDemand = worldState?.routeDemand || {};

  // Update state
  setWorldState(prev => {
    const prevWings = getWingsState(prev);
    let updatedRoutes = [...(prev.routes || [])];
    let abandonedRoutes = { ...(prev.abandonedRoutes || {}) };

    // Remove stale routes if replacing - track them for cooldown
    if (mode === 'replace' && staleRoutes.length > 0) {
      const staleIds = new Set(staleRoutes.map(r => r.id));

      // Track abandoned routes with their current demand and day
      for (const route of staleRoutes) {
        const routeKey = getRouteKey(route.origin, route.destination);
        const currentDemand = routeDemand[routeKey] ?? 100;
        abandonedRoutes[routeKey] = {
          abandonedDay: currentDay,
          demandAtAbandonment: currentDemand,
          cooldownUntilDay: currentDay + WINGS_CONFIG.abandonedRouteCooldownDays,
        };
      }

      updatedRoutes = updatedRoutes.filter(r => !staleIds.has(r.id));
    }

    // Add new routes
    updatedRoutes = [...updatedRoutes, ...newRoutes];

    return {
      ...prev,
      routes: updatedRoutes,
      abandonedRoutes,
      wings: {
        ...prevWings,
        balance: (prevWings.balance || 0) - WINGS_CONFIG.routeGenerationCost,
        lifetimeSpent: (prevWings.lifetimeSpent || 0) + WINGS_CONFIG.routeGenerationCost,
      },
    };
  });

  return {
    success: true,
    newRoutes,
    removedRoutes: staleRoutes,
  };
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function canAffordRouteGeneration(worldState) {
  const wings = getWingsState(worldState);
  return (wings.balance || 0) >= WINGS_CONFIG.routeGenerationCost;
}

export function addWings(amount, setWorldState) {
  setWorldState(prev => ({
    ...prev,
    wings: {
      ...getWingsState(prev),
      balance: (getWingsState(prev).balance || 0) + amount,
      lifetimeEarned: (getWingsState(prev).lifetimeEarned || 0) + amount,
    },
  }));
}

// ============================================================================
// AUTO-DEPART MODE
// ============================================================================

/**
 * Check if auto-depart mode is currently active
 * @returns {Object} { active: boolean, remainingMs: number, expiresAt: number }
 */
export function getAutoDepartStatus(worldState) {
  const expiresAt = worldState?.autoDepart?.expiresAt || 0;
  const now = Date.now();

  if (expiresAt > now) {
    return {
      active: true,
      remainingMs: expiresAt - now,
      expiresAt,
    };
  }

  return {
    active: false,
    remainingMs: 0,
    expiresAt: 0,
  };
}

/**
 * Check if player can afford auto-depart
 */
export function canAffordAutoDepart(worldState) {
  const wings = getWingsState(worldState);
  return (wings.balance || 0) >= WINGS_CONFIG.autoDepartCost;
}

/**
 * Activate auto-depart mode
 * @returns {Object} { success: boolean, error?: string, expiresAt?: number }
 */
export function activateAutoDepart(worldState, setWorldState) {
  const wings = getWingsState(worldState);

  if ((wings.balance || 0) < WINGS_CONFIG.autoDepartCost) {
    return { success: false, error: `Not enough Wings (need ${WINGS_CONFIG.autoDepartCost})` };
  }

  // Check if already active
  const status = getAutoDepartStatus(worldState);
  if (status.active) {
    return { success: false, error: 'Auto-depart is already active' };
  }

  const expiresAt = Date.now() + WINGS_CONFIG.autoDepartDurationMs;

  setWorldState(prev => ({
    ...prev,
    autoDepart: {
      active: true,
      expiresAt,
      activatedAt: Date.now(),
    },
    wings: {
      ...getWingsState(prev),
      balance: (getWingsState(prev).balance || 0) - WINGS_CONFIG.autoDepartCost,
      lifetimeSpent: (getWingsState(prev).lifetimeSpent || 0) + WINGS_CONFIG.autoDepartCost,
    },
  }));

  debug(`[Wings] Auto-depart activated for ${WINGS_CONFIG.autoDepartDurationMs / 1000}s`);

  return { success: true, expiresAt };
}

/**
 * Deactivate auto-depart mode (when timer expires)
 */
export function deactivateAutoDepart(setWorldState) {
  setWorldState(prev => ({
    ...prev,
    autoDepart: {
      active: false,
      expiresAt: 0,
      activatedAt: null,
    },
  }));

  debug('[Wings] Auto-depart deactivated');
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  WINGS_CONFIG,
  MILESTONES,
  createWingsState,
  getWingsState,
  checkMilestones,
  processMilestones,
  processFlightComplete,
  findNewRouteDestinations,
  generateRoutes,
  findStaleRoutes,
  spendWingOnRoutes,
  canAffordRouteGeneration,
  addWings,
  getAutoDepartStatus,
  canAffordAutoDepart,
  activateAutoDepart,
  deactivateAutoDepart,
};
